import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isUserAdminInCurrentOrg } from '@/lib/permissions';
import { useAuth } from './AuthContext';
import { useOrgs } from './OrgsProvider';

interface AdminContextType {
  isAdmin: boolean;
  isLoading: boolean;
  refreshAdminStatus: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { activeOrg } = useOrgs();

  const checkAdminStatus = async () => {
    if (!isAuthenticated || !user) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    // Don't check if org is not yet available
    if (!activeOrg) {
      console.log('Admin status: Waiting for org context to be ready...');
      return;
    }

    try {
      setIsLoading(true);
      const adminStatus = await isUserAdminInCurrentOrg();
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAdminStatus = async () => {
    await checkAdminStatus();
  };

  // Check admin status when user authentication changes AND org is available.
  // Must wait for auth to finish loading — otherwise the initial render with
  // `isAuthenticated=false` (auth not yet checked) would fall into the
  // unauthenticated branch below, flip isLoading to false, and when auth
  // later settles to true before activeOrg arrives, AdminProtectedRoute would
  // see {isLoading:false, isAdmin:false, isAuthenticated:true} and redirect
  // to /dashboard.
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (isAuthenticated && user && activeOrg) {
      checkAdminStatus();
    } else if (!isAuthenticated || !user) {
      setIsAdmin(false);
      setIsLoading(false);
    }
    // else: authed but waiting for activeOrg — keep isLoading true.
  }, [authLoading, isAuthenticated, user?.id, activeOrg?.id]);

  const value = {
    isAdmin,
    isLoading,
    refreshAdminStatus,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
